import React, { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Input, Button, Card, Pagination } from 'antd';
import { useEntries } from '../context/EntriesContext';

const DEFAULT_PAGE_SIZE = 25;
const MOBILE_BREAKPOINT_PX = 768;
const MOBILE_PAGE_SIZE = 10;

function filterBySearch(entries, q) {
  if (!q || !String(q).trim()) return entries;
  const lower = String(q).trim().toLowerCase();
  return entries.filter(
    (r) =>
      (r.player_name && String(r.player_name).toLowerCase().includes(lower)) ||
      (r.set_name && String(r.set_name).toLowerCase().includes(lower)) ||
      (r.tournament && String(r.tournament).toLowerCase().includes(lower)) ||
      (r.region && String(r.region).toLowerCase().includes(lower)) ||
      String(r.placement).includes(lower)
  );
}

const DECK_BUILDER_BASE = 'https://deckbuilder.egmanevents.com/?deck=';

function getDeckListUrl(deckList) {
  if (!deckList || !String(deckList).trim()) return null;
  const encoded = encodeURIComponent(String(deckList).trim());
  return `${DECK_BUILDER_BASE}${encoded}&type=optcg`;
}

const tableColumns = [
  { title: 'Player', dataIndex: 'player_name', key: 'player_name', ellipsis: true },
  { title: 'Format', dataIndex: 'set_name', key: 'set_name', ellipsis: true },
  { title: 'Tournament', dataIndex: 'tournament', key: 'tournament', ellipsis: true },
  { title: 'Region', dataIndex: 'region', key: 'region', ellipsis: true },
  {
    title: 'Placement',
    dataIndex: 'placement',
    key: 'placement',
    align: 'center',
    width: 100,
    defaultSortOrder: 'ascend',
    sorter: (a, b) => (a.placement ?? 0) - (b.placement ?? 0),
  },
  {
    title: 'Deck List',
    key: 'deck_list',
    align: 'center',
    width: 110,
    render: (_, record) => {
      const url = getDeckListUrl(record.deck_list);
      if (!url) return '—';
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-block',
            padding: '4px 10px',
            fontSize: 13,
            fontWeight: 600,
            color: '#1677ff',
            backgroundColor: '#e6f4ff',
            borderRadius: 8,
            textDecoration: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          View deck
        </a>
      );
    },
  },
];

function DeckDetailPage() {
  const { deckName } = useParams();
  const navigate = useNavigate();
  const { entries: allEntries, loading, error } = useEntries();

  const [isNarrow, setIsNarrow] = useState(
    typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT_PX
  );
  const [globalFilter, setGlobalFilter] = useState('');
  const [mobilePage, setMobilePage] = useState(1);

  useEffect(() => {
    const check = () => setIsNarrow(window.innerWidth < MOBILE_BREAKPOINT_PX);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const displayName = decodeURIComponent(deckName || '');
  const entriesForDeck = useMemo(() => {
    if (!allEntries.length || !displayName) return [];
    return allEntries.filter((e) => e.deck === displayName);
  }, [allEntries, displayName]);

  const filteredData = useMemo(
    () => filterBySearch(entriesForDeck, globalFilter),
    [entriesForDeck, globalFilter]
  );

  const sortedForMobile = useMemo(() => {
    return [...filteredData].sort((a, b) => (a.placement ?? 0) - (b.placement ?? 0));
  }, [filteredData]);

  const pageSize = isNarrow ? MOBILE_PAGE_SIZE : DEFAULT_PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(sortedForMobile.length / pageSize));
  const mobileStart = (mobilePage - 1) * pageSize;
  const mobileData = useMemo(
    () => sortedForMobile.slice(mobileStart, mobileStart + pageSize),
    [sortedForMobile, mobileStart, pageSize]
  );

  useEffect(() => {
    if (mobilePage > totalPages) setMobilePage(Math.max(1, totalPages));
  }, [mobilePage, totalPages]);

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
        Loading tops for this deck...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px', color: '#c33', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
        <p>{error}</p>
        <Button type="primary" onClick={() => navigate('/')} style={{ marginTop: 12 }}>
          Back to Tournament Data
        </Button>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: '32px 40px 40px',
        maxWidth: '1600px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        maxHeight: '100vh',
        boxSizing: 'border-box',
        backgroundColor: '#f5f5f5',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
          flexWrap: 'wrap',
          flexShrink: 0,
        }}
      >
        <Button type="primary" size="large" onClick={() => navigate('/')}>
          ← Back to Tournament Data
        </Button>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 600, color: '#333' }}>
          Tops for: {displayName}
        </h1>
        <span style={{ fontSize: 15, color: '#666' }}>
          {filteredData.length} result{filteredData.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div style={{ marginBottom: 20, flexShrink: 0 }}>
        <Input
          placeholder="Player, format, tournament, region, placement..."
          allowClear
          value={globalFilter}
          onChange={(e) => {
            setGlobalFilter(e.target.value);
            setMobilePage(1);
          }}
          size="large"
          style={{ maxWidth: 420 }}
        />
      </div>

      {filteredData.length === 0 ? (
        <p style={{ color: '#666', flexShrink: 0 }}>
          {entriesForDeck.length === 0 ? 'No tops found for this deck.' : 'No results match your search.'}
        </p>
      ) : isNarrow ? (
        <>
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              padding: '4px 0',
            }}
          >
            {mobileData.map((e, i) => (
              <Card key={`${e.player_name}-${e.tournament}-${e.placement}-${mobileStart + i}`} size="small">
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: 12,
                    flexWrap: 'wrap',
                    gap: 8,
                  }}
                >
                  <span style={{ fontSize: 15, fontWeight: 600, color: '#0f1a2a' }}>{e.player_name}</span>
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: '#1677ff',
                      backgroundColor: '#e6f4ff',
                      padding: '4px 10px',
                      borderRadius: 8,
                    }}
                  >
                    #{e.placement}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: '#64748b' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    <span style={{ color: '#94a3b8' }}>Format:</span>
                    <span>{e.set_name}</span>
                  </div>
                  <div>
                    <span style={{ color: '#94a3b8' }}>Tournament:</span>
                    <span style={{ marginLeft: 6 }}>{e.tournament}</span>
                  </div>
                  <div>
                    <span style={{ color: '#94a3b8' }}>Region:</span>
                    <span style={{ marginLeft: 6 }}>{e.region}</span>
                  </div>
                  {getDeckListUrl(e.deck_list) && (
                    <div>
                      <a
                        href={getDeckListUrl(e.deck_list)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-block',
                          marginTop: 4,
                          padding: '6px 12px',
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#1677ff',
                          backgroundColor: '#e6f4ff',
                          borderRadius: 8,
                          textDecoration: 'none',
                        }}
                      >
                        View deck
                      </a>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
          <div
            style={{
              marginTop: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 12,
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
              Showing {mobileStart + 1}–{Math.min(mobileStart + pageSize, sortedForMobile.length)} of{' '}
              {sortedForMobile.length}
            </span>
            <Pagination
              current={mobilePage}
              total={sortedForMobile.length}
              pageSize={pageSize}
              showSizeChanger={false}
              onChange={setMobilePage}
              showTotal={() => `Page ${mobilePage} of ${totalPages}`}
            />
          </div>
        </>
      ) : (
        <Card
          style={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 12,
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          }}
          bodyStyle={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div className="deck-detail-table-scroll" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <Table
              dataSource={filteredData.map((r, i) => ({ ...r, key: `${r.player_name}-${r.tournament}-${r.placement}-${i}` }))}
              columns={tableColumns}
              pagination={{
                pageSize: DEFAULT_PAGE_SIZE,
                showSizeChanger: false,
                showTotal: (total, range) =>
                  `Showing ${range[0]}–${range[1]} of ${total}`,
                size: 'default',
              }}
              size="large"
              className="deck-detail-table"
            />
          </div>
        </Card>
      )}
    </div>
  );
}

export default DeckDetailPage;
